import { useRef } from 'react'
import { average, mappedArray } from '@10000/common'
import { useBreakpoints, useDelayedValue, useView } from '@10000/react'
import { BarGraph, Body, Box, CenteredBox, Expertise, H4, ElementHighlight } from '../widgets'
import { practices, questions, isFree } from '../../lookups'
import { TwoColumnView } from '../layout'
import { PaidContent, PaidContentDialog } from '../free'
import { useAnalyticsData } from '.'
import { padZero } from '../../utilities.js'
import './PracticeBreakdown.scss'

export const PracticeBreakdown = ({ displayMode, onClose, ...props }) => {
  const { active, practice, answer, setAnswer } = useAnalyticsData()
  const { name, subpractices = [] } = practices[practice] || {}
  const buttonsRef = useRef()
  const { mobile } = useBreakpoints()

  const toggleAnswer = selectedAnswer => setAnswer(selectedAnswer === answer ? undefined : selectedAnswer)

  const subPracticeGuide = (practice === undefined || isNaN(average(active?.teamAverage?.[practice * 2]))) ? 1 : 0

  return <>
    <TwoColumnView
      startingColumn={subPracticeGuide}
      title={name}
      ref={buttonsRef}
      refButtonIndex={subPracticeGuide === 0 ? 1 : 0}
      onClose={onClose}
      displayMode={displayMode}
      className="practice-breakdown"
      column1={
        <Subpractice
          practice={practice}
          subpractice={0}
          showSubPracticeGuide={subPracticeGuide === 0}
          values={active?.teamAverage?.[practice * 2]}
          consensus={active?.consensus?.[practice * 2]}
          change={active?.teamAverageChange?.[practice * 2]}
          selectedAnswer={answer}
          onAnswerSelected={displayMode ? undefined : toggleAnswer}
        />
      }
      column1Label={subpractices[0]?.title}
      column2={
        <Subpractice
          practice={practice}
          subpractice={1}
          showSubPracticeGuide={subPracticeGuide === 1}
          values={active?.teamAverage?.[practice * 2 + 1]}
          consensus={active?.consensus?.[practice * 2 + 1]}
          change={active?.teamAverageChange?.[practice * 2 + 1]}
          selectedAnswer={answer}
          onAnswerSelected={displayMode ? undefined : toggleAnswer}
        />
      }
      column2Label={subpractices[1]?.title}
      {...props}
    />

    {mobile && <ElementHighlight
      order={2}
      nextOnFinish
      target={buttonsRef}
      pointDirection="up"
    />}
  </>
}

const Subpractice = ({ practice, subpractice, values = [], consensus, change, selectedAnswer, onAnswerSelected, showSubPracticeGuide }) => {
  const { title = '', description } = practices[practice]?.subpractices?.[subpractice] || {}
  const { question, answers } = questions[practice * 2 + subpractice] || {}
  const { touches, timeTaken } = consensus || {}
  const distribution = mappedArray(6, i => values.filter(x => x === i).length / values.length)
  const visibleSelectedAnswer = useDelayedValue(selectedAnswer, { undefined: 250 })
  const { subscription } = useView('tenant')

  const graph = useRef()
  const { mobile } = useBreakpoints()

  return <Box
    container
    column
    alignItems="center"
    pl={(subpractice === 0 && mobile) ? 16 : 32}
    pr={32}
    gap={mobile && 16}
  >
    {(!showSubPracticeGuide && isFree(subscription)) && <>
      <PaidContent />
      <PaidContentDialog />
    </>}

    <Expertise
      title={title.toUpperCase()}
      expertiseSize={18}
      value={average(values)}
      change={change}
      column={false}
      fullWidth
      gap={24}
      py={16}
      justifyContent="center"
      className="desktop-only"
    />
    <Expertise
      value={average(values)}
      expertiseSize={16}
      change={change}
      column={false}
      fullWidth
      gap={24}
      justifyContent="center"
      className="mobile-only"
    />

    <Body alt bold align="center">
      {question}
    </Body>
    <Body align="center">
      {description}
    </Body>

    <Box column container fullWidth m={-10} p={10}>
      <Box className="graph" ref={graph} r={16}>
        <BarGraph
          values={distribution}
          selectedValue={selectedAnswer}
          onClick={onAnswerSelected}
        />
      </Box>

      {touches > 0
        ? <CenteredBox container fullWidth gap={4} className="consensus" mt={40}>
            <Body alt bold>CONSENSUS MODE</Body>

            <Box>
              <Box column gap={4} alignItems="center">
                <Body alt bold size={16} color="#727272">
                  Touches
                </Body>
                <Body alt bold size={24}>
                  {touches}
                </Body>
              </Box>

              <Box column gap={4} alignItems="center">
                <Body alt bold size={16} color="#727272" align="center">
                  Decision time
                </Body>
                <H4>
                  {Math.floor(timeTaken / 1000 / 60)}:{padZero(Math.floor(timeTaken / 1000 % 60))}
                </H4>
              </Box>
            </Box>
          </CenteredBox>
        : <Box height={48} />
      }

      <Box
        container
        fullWidth
        r={16}
        p={16}
        pb={16}
        background="black"
        border="1px solid white"
        shadow="2px 2px 0px 0px white"
        opacity={selectedAnswer === undefined ? 0 : 1}
        transition={0.25}
      >
        <Body alt weight={600} lineHeight="normal">
          {answers?.[visibleSelectedAnswer]}
        </Body>
      </Box>
    </Box>

    {showSubPracticeGuide &&
      <ElementHighlight
        order={1}
        target={graph}
        pointDirection={mobile ? 'down' : subpractice === 0 ? 'left-down' : 'right-down'}
        nextOnFinish
      />
    }
  </Box>
}
